import { Button, Form, Input, Popconfirm, Tag, notification } from 'antd';
import { useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import styles from './member-modal.module.scss';

type Account = { id: number; nickName?: string; isVerifyAccount?: boolean; isAccountDisabled?: boolean };
type Action = 'verify' | 'lock' | 'unlock';

/** Email / Khoá chạy ngay (có hỏi lại), không đợi nút Lưu; mật khẩu mở khi cần. */
export default function SecuritySection({
  account,
  onChanged,
}: {
  account: Account;
  onChanged: (patch: Partial<Account>) => void;
}) {
  const [busy, setBusy] = useState<Action | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const who = account.nickName || `ID ${account.id}`;

  const run = async (key: Action, path: string, patch: Partial<Account>, done: string) => {
    setBusy(key);
    try {
      await AxiosUtils.post(`${API.users}/${account.id}/${path}`, {});
      onChanged(patch);
      notification.success({ message: done, duration: 3 });
    } catch (error: any) {
      const message = Array.isArray(error?.message) ? error.message.join(', ') : error?.message;
      notification.error({ message: 'Không thực hiện được', description: message || 'Vui lòng thử lại.' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={styles.securityList}>
      <div className={styles.securityRow}>
        <div>
          <div className={styles.toggleLabel}>Email</div>
          <div className={styles.hint}>Người dùng tự xác minh bằng mã gửi về email khi đăng ký.</div>
        </div>
        <div className={styles.securityAction}>
          <Tag color={account.isVerifyAccount ? 'success' : 'warning'}>
            {account.isVerifyAccount ? 'Đã xác minh' : 'Chưa xác minh'}
          </Tag>
          {!account.isVerifyAccount ? (
            <Popconfirm
              title={`Xác minh email thay cho ${who}?`}
              description="Chỉ làm khi đã chắc chắn email này đúng của người đó."
              okText="Xác minh"
              cancelText="Hủy"
              onConfirm={() => run('verify', 'verify-email', { isVerifyAccount: true }, 'Đã xác minh email')}
            >
              <Button size="small" loading={busy === 'verify'}>
                Xác minh thủ công
              </Button>
            </Popconfirm>
          ) : null}
        </div>
      </div>

      <div className={styles.securityRow}>
        <div>
          <div className={styles.toggleLabel}>Trạng thái</div>
          <div className={styles.hint}>Khoá: không đăng nhập được, mọi phiên đang mở bị đăng xuất.</div>
        </div>
        <div className={styles.securityAction}>
          <Tag color={account.isAccountDisabled ? 'error' : 'success'}>
            {account.isAccountDisabled ? 'Đang khoá' : 'Đang hoạt động'}
          </Tag>
          {account.isAccountDisabled ? (
            <Popconfirm
              title={`Mở khoá ${who}?`}
              okText="Mở khoá"
              cancelText="Hủy"
              onConfirm={() => run('unlock', 'unlock', { isAccountDisabled: false }, 'Đã mở khoá tài khoản')}
            >
              <Button size="small" loading={busy === 'unlock'}>
                Mở khoá
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title={`Khoá ${who}?`}
              description="Người này bị đăng xuất khỏi mọi thiết bị và không đăng nhập được."
              okText="Khoá"
              okButtonProps={{ danger: true }}
              cancelText="Hủy"
              onConfirm={() => run('lock', 'lock', { isAccountDisabled: true }, 'Đã khoá tài khoản')}
            >
              <Button size="small" danger loading={busy === 'lock'}>
                Khoá tài khoản
              </Button>
            </Popconfirm>
          )}
        </div>
      </div>

      <div className={styles.securityRow}>
        <div>
          <div className={styles.toggleLabel}>Mật khẩu</div>
          <div className={styles.hint}>Chỉ đặt khi người dùng nhờ; lưu cùng nút "Lưu thay đổi".</div>
        </div>
        <div className={styles.securityAction}>
          {!showPassword ? (
            <Button size="small" onClick={() => setShowPassword(true)}>
              Đặt mật khẩu mới
            </Button>
          ) : null}
        </div>
      </div>
      {showPassword ? (
        <Form.Item
          name="password"
          label="Mật khẩu mới"
          rules={[
            { min: 8, message: 'Mật khẩu cần ít nhất 8 kí tự' },
            { max: 15, message: 'Mật khẩu cần ít hơn 16 kí tự' },
          ]}
        >
          <Input.Password autoComplete="new-password" placeholder="8–15 kí tự" autoFocus />
        </Form.Item>
      ) : null}
    </div>
  );
}
